import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Skeleton,
  Alert,
  LinearProgress,
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useMissionInstances } from '../hooks/useMissions';
import { useMissionBuilder } from '../MissionBuilderContext';
import type { InstanceStatus } from '../types';

const STATUS_COLORS: Record<InstanceStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  pending: 'default',
  active: 'info',
  completed: 'success',
  expired: 'warning',
  cancelled: 'error',
};

export function InstanceList() {
  const { selectedTemplate, setView } = useMissionBuilder();

  if (!selectedTemplate) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No template selected</Typography>
        <Button onClick={() => setView('list')} sx={{ mt: 1 }}>Back to list</Button>
      </Box>
    );
  }

  return <InstanceListInner missionId={selectedTemplate.id} missionName={selectedTemplate.name} />;
}

function InstanceListInner({ missionId, missionName }: { missionId: string; missionName: string }) {
  const { data, loading, error, refresh } = useMissionInstances(missionId);
  const { setView } = useMissionBuilder();

  if (loading && data.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={80} />)}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Button onClick={() => refresh()}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => setView('list')} size="small">
          Back
        </Button>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Instances: {missionName}
        </Typography>
      </Box>

      {data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No instances yet. Trigger the mission to create one.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {data.map((instance) => {
            const progress = instance.totalItems > 0
              ? Math.round((instance.completedCount / instance.totalItems) * 100)
              : 0;

            return (
              <Paper key={instance.id} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Chip label={instance.status} size="small" color={STATUS_COLORS[instance.status]} />
                  <Typography variant="body2" color="text.secondary">
                    {instance.assignee?.displayName || instance.assignedTo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {new Date(instance.createdAt).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ flex: 1, height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 60, textAlign: 'right' }}>
                    {instance.completedCount}/{instance.totalItems}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
